import { useContext, useEffect, useCallback } from "react";
import { SocketContext } from "src/contexts/SocketContext";
import useAppStore from "src/stores/appStore";
import { assistantIdSelector } from "src/stores/selectors/appSelectors";

const EVENTS = {
  CONNECT: "connect",
  REGISTER: "notifications:register",
  NOTIFY: "notifications:notify",
  HISTORY_COUNT: "notifications:history:count",
  HISTORY_LIST: "notifications:history:list",
  MARK_AS_READ: "notifications:mark-as-read",
  ERROR: "error"
};

const REGISTERED_ASSISTANT_ID_KEY = "registeredAssistantId";

const useNotificationsSocket = () => {
  const assistantId = useAppStore(assistantIdSelector);
  const socket = useContext(SocketContext);

  const {
    addNotifications,
    upsertNotification,
    setNotificationsCount,
    setHasMoreNotifications,
    setIsInitialNotificationsLoading,
    setIsFetchingNextNotificationsPage,
    setFetchNextNotificationsPage,
    setMarkNotificationsAsReadViaSocket,
    incrementNotificationsPage
  } = useAppStore();

  const emitInitializationEvents = useCallback(() => {
    const registeredAssistantId = socket[REGISTERED_ASSISTANT_ID_KEY];
    if (registeredAssistantId === assistantId) return;

    socket.emit(EVENTS.REGISTER, { assistantId });
  }, [socket, assistantId]);

  const fetchNextPage = useCallback(() => {
    setIsFetchingNextNotificationsPage(true);
    const currentState = useAppStore.getState();

    socket.emit(EVENTS.HISTORY_LIST, {
      assistantId,
      pageNumber: currentState.notificationsPage + 1,
      fromDate: currentState.notifications.at(0)?.createdAt
    });

    incrementNotificationsPage();
  }, [socket, assistantId, setIsFetchingNextNotificationsPage, incrementNotificationsPage]);

  const handleHistoryList = useCallback(({ notifications, hasMore }) => {
    addNotifications(notifications);
    setHasMoreNotifications(hasMore);
    setIsInitialNotificationsLoading(false);
    setIsFetchingNextNotificationsPage(false);
  }, [addNotifications, setHasMoreNotifications, setIsInitialNotificationsLoading, setIsFetchingNextNotificationsPage]);

  const handleNewNotification = useCallback((newNotification) => {
    upsertNotification(newNotification);
    socket.emit(EVENTS.HISTORY_COUNT, { assistantId });
  }, [upsertNotification, socket, assistantId]);

  const handleHistoryCount = useCallback((count) => {
    setNotificationsCount(count);
  }, [setNotificationsCount]);

  const handleRegisterSuccess = useCallback(() => {
    setIsInitialNotificationsLoading(true);
    socket[REGISTERED_ASSISTANT_ID_KEY] = assistantId;
    socket.emit(EVENTS.HISTORY_COUNT, { assistantId });
    socket.emit(EVENTS.HISTORY_LIST, { assistantId });
  }, [socket, assistantId, setIsInitialNotificationsLoading]);

  const emitMarkAsRead = useCallback(() => {
    socket.emit(EVENTS.MARK_AS_READ, { assistantId, untilDate: new Date().toISOString() });
    setNotificationsCount(0);
  }, [socket, assistantId, setNotificationsCount]);

  const handleSocketError = useCallback((error) => {
    console.error("Socket error:", error);
  }, []);

  useEffect(() => {
    if (!socket || !assistantId) return;

    socket.on(EVENTS.CONNECT, emitInitializationEvents);
    socket.on(EVENTS.REGISTER, handleRegisterSuccess);
    socket.on(EVENTS.HISTORY_LIST, handleHistoryList);
    socket.on(EVENTS.HISTORY_COUNT, handleHistoryCount);
    socket.on(EVENTS.NOTIFY, handleNewNotification);
    socket.on(EVENTS.ERROR, handleSocketError);

    if (socket.connected) {
      emitInitializationEvents();
    }

    setFetchNextNotificationsPage(fetchNextPage);
    setMarkNotificationsAsReadViaSocket(emitMarkAsRead);
    incrementNotificationsPage();

    // eslint-disable-next-line consistent-return
    return () => {
      socket.off(EVENTS.CONNECT, emitInitializationEvents);
      socket.off(EVENTS.REGISTER, handleRegisterSuccess);
      socket.off(EVENTS.HISTORY_LIST, handleHistoryList);
      socket.off(EVENTS.HISTORY_COUNT, handleHistoryCount);
      socket.off(EVENTS.NOTIFY, handleNewNotification);
      socket.off(EVENTS.ERROR, handleSocketError);
    };
  }, [
    socket,
    assistantId,
    handleRegisterSuccess,
    handleHistoryList,
    handleHistoryCount,
    handleNewNotification,
    handleSocketError,
    emitInitializationEvents,
    fetchNextPage,
    setFetchNextNotificationsPage,
    setMarkNotificationsAsReadViaSocket,
    emitMarkAsRead,
    incrementNotificationsPage
  ]);
};

export default useNotificationsSocket;
